import React from 'react'

import { IProps as IIconCard } from 'marketing-site/src/mvp/library/elements/IconCard'
import { CrosslinkCards } from 'marketing-site/src/mvp/library/components/CrosslinkCards'
import { HeroVertical } from 'marketing-site/src/mvp/library/components/HeroVertical'
import { IconGrid } from 'marketing-site/src/mvp/library/components/IconGrid'
import { LargeTestimonial } from 'marketing-site/src/mvp/library/components/LargeTestimonial'
import { PreFooterCTABlock } from 'marketing-site/src/mvp/library/components/PreFooterCTABlock'
import { SpotlightDrawers } from 'marketing-site/src/mvp/library/components/SpotlightDrawers'
import { IconGridItem } from 'marketing-site/src/mvp/library/elements/IconGridItem'
import { ICapabilityPage } from '.'
import { CTATheme, Icon } from 'marketing-site/src/mvp/library/utils'
import useTranslate from 'marketing-site/src/library/hooks/useTranslate'
import { CTALink } from 'marketing-site/src/mvp/library/elements/CTALink'
import { Image } from 'marketing-site/src/library/elements/Image'

export function CapabilityPage(page: ICapabilityPage): React.ReactElement {
  const { translate } = useTranslate()

  return (
    <div data-testid="capability-page">
      <HeroVertical
        eyebrow={page.capability.name}
        title={page.title}
        subhead={page.subtitle}
        media={page.capability.media}
        mediaWidth="inset"
        ctas={[
          <CTALink
            key={page.primaryLink.title}
            url={page.primaryLink.url}
            text={page.primaryLink.title}
            variant={CTATheme.BlueFill}
            icon={Icon.Arrow}
          />,
        ]}
      />

      <SpotlightDrawers
        eyebrow={translate('Key Features')}
        title={page.capability.keyFeatures.title}
        slides={page.capability.keyFeatures.items.map((feature) => ({
          icon: <Image {...feature.icon} />,
          title: feature.title,
          subhead: feature.description,
          media: feature.image && <Image {...feature.image} />,
        }))}
        layout="media-right"
        backgroundColor="slate"
      />

      {page.capability.secondaryFeatures && page.capability.secondaryFeatures?.items.length > 0 && (
        <IconGrid
          layout={page.capability.secondaryFeatures.items.length % 4 === 0 ? '4-4' : '3-3'}
          title={translate(`More ${page.capability.name} features`)}
          subhead={page.capability.secondaryFeatures.title}
          items={page.capability.secondaryFeatures.items.map((item) => (
            <IconGridItem
              key={item.title}
              subhead={item.description}
              icon={<Image {...item.icon} />}
              title={item.title}
              cta={
                item.link && (
                  <CTALink
                    variant={CTATheme.LinkOnlySlate}
                    icon={Icon.Arrow}
                    text={item.link.title}
                    url={item.link.url}
                  />
                )
              }
            />
          ))}
        />
      )}

      <LargeTestimonial
        quote={page.capability.testimonial.quote}
        image={page.capability.testimonial.portraitImgRef}
        companyLogo={page.capability.testimonial.companyLogo}
        authorName={page.capability.testimonial.authorName}
        authorRole={page.capability.testimonial.authorRole}
      />

      <CrosslinkCards
        eyebrow={page.capability.solutionsEyebrow}
        heading={page.capability.solutionsHeading}
        subheading={page.capability.solutionsSubheading}
        backgroundColor="sky"
        cards={page.capability.solutions.map((item) => {
          const iconCard: IIconCard = {
            icon: item.icon,
            title: item.heading,
            subhead: item.subheading,
            cta: {
              variant: CTATheme.LinkOnlySlate,
              text: item.ctaText,
              url: item.ctaUrl,
            },
            contentType: 'iconCard',
          }
          return iconCard
        })}
      />

      <PreFooterCTABlock
        title={page.footerText}
        ctaContentType="ctaLink"
        ctas={[
          <CTALink
            key={0}
            variant={CTATheme.BlueFill}
            text={page.footerLink.title}
            url={page.footerLink.url}
            icon="Arrow"
          />,
        ]}
        backgroundColor="slate"
      />
    </div>
  )
}
